import CopyIcon from '@src/components/icons/copy';
import type { IIndexHighlight, IProposalExpireAt } from '@src/models/types';
import { getDaysUntilExpiry } from '@src/utils/date';
import Badge from '@src/components/atoms/badges/badge';
import { toast } from 'react-toastify';
import { ToastId } from '@src/constants/toastId';
import { capitalize } from '@src/utils/stringUtils';

interface IGovernanceActionSearchCard {
  title: string;
  abstract: string;
  type: string;
  id: string;
  expireAt: IProposalExpireAt;
  highlight: IIndexHighlight;
  onCardClick: (id: string) => void;
}

export default function GovernanceActionSearchCard({
  title,
  abstract,
  type,
  id,
  expireAt,
  highlight,
  onCardClick,
}: IGovernanceActionSearchCard) {
  const isExpiredProposal = new Date(expireAt.time) < new Date();

  const expiryDays = getDaysUntilExpiry(expireAt.time);

  const CardField = ({
    label,
    value,
    showCopyIcon,
  }: {
    label: string;
    value: string;
    showCopyIcon?: boolean;
  }) => (
    <div className="flex items-start">
      <div className="body16 w-[200px] min-w-[200px] font-semibold !leading-6 text-white">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <span
          className="body14 purple-em line-clamp-1 leading-4 text-gray-400"
          dangerouslySetInnerHTML={{ __html: value }}
        ></span>
        {showCopyIcon && (
          <CopyIcon
            onClick={() => {
              handleCopy(value);
            }}
          />
        )}
      </div>
    </div>
  );

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success('Copied to clipboard', { toastId: ToastId.SUCCESS_TOAST });
  };

  return (
    <div
      className="w-full overflow-clip rounded-md hover:cursor-pointer"
      onClick={() => {
        onCardClick(id);
      }}
    >
      <div className="flex w-full justify-between bg-dark-neutral-600 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="body18 font-bold text-white">{title}</span>

          <Badge
            className="w-fit"
            label={isExpiredProposal ? 'Expired' : 'Live'}
            color={isExpiredProposal ? 'error' : 'success'}
          />
        </div>
        {!isExpiredProposal && (
          <span className="body14 rounded-xl border-[1px] border-dark-neutral-700 bg-dark-neutral-900 p-4 font-semibold leading-5 text-white">
            {expiryDays}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3 rounded-md rounded-t-none border-[1px] border-t-0 border-dark-neutral-700 p-4">
        <div className="flex justify-between py-2">
          <div className="body14 font-medium !leading-5 text-white">
            Governance Action Type: &nbsp;{' '}
            <span className="rounded-full bg-white px-2 py-[2px] text-black">
              {type}
            </span>
          </div>
        </div>
        <CardField label="Governance Action ID : " value={id} showCopyIcon />
        {objectDoesNotContainKey('Abstract', highlight || {}) && (
          <CardField label="Abstract" value={abstract} />
        )}
        {highlight &&
          Object.entries(highlight).map(
            ([key, value]) =>
              !key.includes('search_as_you_type') && (
                <CardField
                  label={capitalize(getCleanedKeyword(key))}
                  value={value.join('...')}
                  key={key}
                />
              )
          )}
      </div>
    </div>
  );
}

const getCleanedKeyword = (keyword: string) => {
  const strList = keyword.split('.');

  if (strList.length === 3 || strList.length === 2) {
    return strList[1];
  }
  return keyword;
};

const objectDoesNotContainKey = (searchKey: string, obj: object) => {
  const searchKeyLower = searchKey.toLowerCase();

  return !Object.keys(obj).some((objKey) =>
    objKey.toLowerCase().includes(searchKeyLower)
  );
};
